import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { lang } from 'moment';
import { BookingAttachmentFile } from '../booking-attachment-file';
import { BookingEmailTemplate } from '../booking-email-template';
import { HttpService } from '../service/http.service';
import { Language } from '../language';
import { SchoolContact } from '../school-contact';
import { StudentContact } from '../student-contact';
import { ToastMessageService } from '../toast-message.service';
import { BookingService } from './booking.service';
import { EmailSignatureService } from './email-signature.service';

@Injectable({
  providedIn: 'root'
})
export class BookingEmailService {

  schoolContacts: SchoolContact[] = [];
  studentContacts: StudentContact[] = [];
  emailAttachmentFiles: BookingAttachmentFile[] = [];
  displayFileAttachmentForm = false;
  emailSending = false;
  displayMailingDialog = false;
  emailTemplates: BookingEmailTemplate[] = [];
  fileAttachmentForm: FormGroup[] = [];
  emailAttachments = [];
  emailForm!: FormGroup;
  loadingEmailTemplate = false;
  emailDialogHeader!: string;
  isEmailToStudent!: boolean;
  ENGLISH = new Language(2, "english");
  loadedTemplate: BookingEmailTemplate | null = null;

  constructor(
    private httpService: HttpService,
    private builder: FormBuilder,
    private bookingService: BookingService,
    private emailSignatureService: EmailSignatureService,
    private toastService: ToastMessageService
  ) { }

  get emailRecipients(): FormArray {
    return this.emailForm.get("recipients") as FormArray;
  }

  loadEmailTemplate(languageId: number) {
    this.httpService.getBookingEmailTemplatesByLanguage(languageId).subscribe(res => {
      this.emailTemplates = res;
    });
  }

  openStudentFileAttachmentDialog(bookingId: number) {
    this.displayFileAttachmentForm = true;
    this.httpService.getBookingStudentAttachmentsByBookingId(bookingId).subscribe(res => {
      this.initFileAttachmentForm(res);
      console.log(res);
    });
  }

  openSchoolFileAttachmentDialog(bookingId: number){
    this.displayFileAttachmentForm = true;
    this.httpService.getBookingSchoolAttachmentsByBookingId(bookingId).subscribe(res => {
      this.initFileAttachmentForm(res);
    });
  }

  initFileAttachmentForm(files: BookingAttachmentFile[]){
    this.fileAttachmentForm = [];
    for (let attachmentFile of files) {
      this.fileAttachmentForm.push(this.builder.group({
        file: attachmentFile,
        isSelected: false
      }));
    }
    this.displayFileAttachmentForm = true;
  }

  activateGeneralEmailDialog(isEmailToStudent: boolean) {
    this.displayMailingDialog = true;
    this.emailAttachmentFiles = [];
    this.isEmailToStudent = isEmailToStudent;
    this.initEmailForm(isEmailToStudent);
    if(isEmailToStudent){
      this.initStudentContacts();
      this.emailDialogHeader = "Send email to: " + this.bookingService.booking.student.lastName + ' ' + this.bookingService.booking.student.firstName;
    } else {
      this.initSchoolContacts();
      this.emailDialogHeader = "Send email to school";
    }
    this.loadEmailTemplate(this.bookingService.booking.student.language.id);
  }

  initSchoolContacts(){
    if(this.bookingService.booking.school){
      this.httpService.getSchoolContacts(this.bookingService.booking.school!.id!).subscribe(res => {
        this.schoolContacts = res;
        for(let contact of this.schoolContacts){
          this.emailRecipients.push(this.builder.group({
            email: contact.email,
            isSelected: false,
            typeName: contact.schoolContactType.name
          }));
        }
      });
    }
  }

  initStudentContacts(){
    if(this.bookingService.booking.student.email){
      this.emailRecipients.controls.push(this.builder.group({
        email: this.bookingService.booking.student.email,
        isSelected: false,
        typeName: this.bookingService.booking.student.primaryContactType.name
      }));
    }
    for(let studentContact of this.bookingService.booking.student.contacts){
      if(studentContact.email){
        this.emailRecipients.controls.push(this.builder.group({
          email: studentContact.email,
          isSelected: false,
          typeName: studentContact.type.name
        }));
      }
    }
  }

  initEmailForm(isEmailToStudent: boolean) {
    let language = null;
    if(isEmailToStudent){
      language = this.bookingService.booking.student.language;
    } else {
      language = this.ENGLISH;
    }

    this.emailForm = this.builder.group({
      subject: null,
      body: null,
      from: null,
      sendTo: null,
      emailTemplate: null,
      signature: this.emailSignatureService.getEmailSignatureByLanguage(language),
      recipients: this.builder.array([]),
      withPastePixel: false,
      backgroundColor: null
    });
  }

  insertEmailTemplate(template: BookingEmailTemplate) {
    this.loadingEmailTemplate = true;
    this.httpService.getCompletedBookingEmailTemplate(template, this.bookingService.booking.id!).subscribe(res => {
      console.log(res);
      this.emailForm.get("subject")!.setValue(res.subject);
      this.emailForm.get("body")!.setValue(res.body);
      this.emailForm.get("backgroundColor")!.setValue(res.backgroundColor);
      this.loadingEmailTemplate = false;
      this.loadedTemplate = template;
    }, error => {
      this.toastService.addError("Error", error.message);
      this.loadingEmailTemplate = false;
    });
  }

  closeFileAttachmentDialog() {
    this.displayFileAttachmentForm = false;
  }

  addEmailAttachmentFile(file: BookingAttachmentFile) {
    const index = this.emailAttachmentFiles.indexOf(file);
    if (index == -1) {
      this.emailAttachmentFiles.push(file);
    }
  }

  removeEmailAttachmentFile(file: BookingAttachmentFile) {
    const index = this.emailAttachmentFiles.indexOf(file);
    if (index != -1) {
      this.emailAttachmentFiles.splice(index, 1);
    }
  }

  updateFilesToAttach(form: any) {
    if (form.value.isSelected) {
      this.addEmailAttachmentFile(form.value.file);
    } else {
      this.removeEmailAttachmentFile(form.value.file);
    }
  }
}
